/* eslint-disable react-hooks/rules-of-hooks */
import { FC, useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NotificationManager } from "../../services/api/NotificationManager";
import {
  GetNotificationSettingsResponse,
  UpdateNotificationSettingsRequest,
} from "../../shared/payloads/notificationSettingsPayloads";
import { showFlashMessageWithTimeout } from "../../services/actions/flashMessage-actions";
import {
  transformFormsToUrlFilterDto,
  transformUrlFilterToForms,
} from "../../generic-utils";
import DOMPurify from "dompurify";
import { Language } from "../../shared/types/websiteTypes";
import { BorderType } from "../../components/NotificationEditComponents/BorderSelect";
import { NotificationDefaultSettings } from "../../types/notification-default-settings";
import { useTranslation } from "react-i18next";
import { getActualVisitorsModalInitialText } from "../../utils/web-utils";
import { v4 } from "uuid";
import { Branding } from "../../shared/types/commonTypes";
import { useNotificationManager } from "../../hooks/useNotificationManager";
import Tabs from "../../components/Tabs";
import { NotificationDesignTab } from "../../components/notification/tabs/NotificationDesignTab";
import { NotificationModalBase } from "../../components/notification/NotificationModalBase";
import { NotificationPairingTab } from "../../components/notification/tabs/NotificationPairingTab";
import { addEmptyPairingForm } from "../../utils/forms/addEmptyPairingForm";
import { handlePairingFormControlChange } from "../../utils/forms/handlePairingFormControlChange";
import { onAddPairingFormExcludeChip } from "../../utils/forms/onAddPairingFormExcludeChip";
import { onAddPairingFormIncludeChip } from "../../utils/forms/onAddPairingFormIncludeChip";
import { onDeletePairingForm } from "../../utils/forms/onDeletePairingForm";
import { NotificationSettingsTab } from "../../components/notification/tabs/NotificationSettingsTab";
import { NotificationRulesTab } from "../../components/notification/tabs/NotificationRulesTab";
import { getCustomWebLogo } from "../../services/selectors/web-selectors";

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaveSuccess?: () => void;
  createdNotificationId: string;
  language: Language;
  openedFor?: "visitorSummary" | "registrationSummary" | "orderSummary";
  data?: GetNotificationSettingsResponse;
  branding: Branding;
}

type ControlType = "beginsWith" | "contains" | "isEqual";

interface FormType {
  control: ControlType;
  include: string[];
  excludes: string[];
}

type TabType = "rules" | "pairing" | "design" | "settings";
type VariantType = "visitorSummary" | "registrationSummary" | "orderSummary";

const getMin = (data: GetNotificationSettingsResponse) => {
  if (data.type === "visitorSummary" && data.minVisitors !== undefined) {
    return data.minVisitors;
  }
  if (
    data.type === "registrationSummary" &&
    data.minRegistrations !== undefined
  ) {
    return data.minRegistrations;
  }
  if (data.type === "orderSummary" && data.minOrders !== undefined) {
    return data.minOrders;
  }
  return 1;
};

const notificationStyleId = v4();

const SummaryModal: FC<SummaryModalProps> = ({
  isOpen,
  onClose,
  onSaveSuccess,
  createdNotificationId,
  language,
  data,
  branding,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { createNotificationPrimitive } = useNotificationManager();

  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<TabType>("rules");
  const [text, setText] = useState<string>(
    data?.text
      ? data.text
      : t(getActualVisitorsModalInitialText(language)) ?? "",
  );

  const [withRepeat, setWithRepeat] = useState<boolean>(
    data?.repeatNotification !== undefined ? data?.repeatNotification : true,
  );
  const [days, setDays] = useState<number>(
    data?.maxLastDays !== undefined ? data?.maxLastDays : 7,
  );
  const [minNumber, setMinNumber] = useState<number>(
    data !== undefined ? getMin(data) : 20,
  );
  const [urlFilterForms, setUrlFilterForms] = useState<FormType[]>(
    data?.urlFilter && Object.keys(data?.urlFilter).length !== 0
      ? transformUrlFilterToForms(data.urlFilter)
      : [{ control: "contains", include: ["/"], excludes: [] }],
  );

  const [selectedVariant, setSelectedVariant] = useState<VariantType>(
    data?.type ? (data.type as VariantType) : "visitorSummary",
  );

  const [notificationHtml, setNotificationHtml] = useState<string>("");

  const [backgroundColor, setBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.backgroundColor || null,
  );
  const [textColor, setTextColor] = useState<string | null>(
    data?.notificationVisualSettings?.textColor || null,
  );
  const [iconBackgroundColor, setIconBackgroundColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconBackgroundColor || null,
  );
  const [iconColor, setIconColor] = useState<string | null>(
    data?.notificationVisualSettings?.iconColor || null,
  );
  const [borderRadius, setBorderRadius] = useState<BorderType | null>(
    data?.notificationVisualSettings?.roundness || null,
  );
  const [icon, setIcon] = useState<string | null>(
    data?.notificationVisualSettings?.icon || null,
  );

  const [backgroundOpacity, setBackgroundOpracity] = useState<number | null>(
    null,
  );
  const [active, setActive] = useState<"classic" | "small" | "compact">(
    "classic",
  );
  const [showIcon, setShowIcon] = useState<boolean>(true);
  const [iconBackgroundRadius, setIconBackgroundRadius] = useState<
    number | null
  >(null);

  const [detectedChange, setDetectedChange] = useState<boolean>(false);

  const customWebBranding = useSelector(getCustomWebLogo);

  useEffect(() => {
    const defaultBackgroundColor =
      data?.notificationVisualSettings?.backgroundColor ??
      branding?.backgroundColor ??
      NotificationDefaultSettings.BackgroundColor;

    const defaultTextColor =
      data?.notificationVisualSettings?.textColor ??
      branding?.textColor ??
      NotificationDefaultSettings.TextColor;

    const daysDefault = data?.maxLastDays !== undefined ? data?.maxLastDays : 7;
    const minNumberDefault = data !== undefined ? getMin(data) : 20;
    const textDefault = data?.text
      ? data.text
      : t(getActualVisitorsModalInitialText(language)) ?? "";

    const activeDefault =
      data?.notificationVisualSettings?.activeSize ?? branding?.activeSize;

    const selectedVariantDefault = data?.type
      ? (data.type as VariantType)
      : "visitorSummary";

    const formsDefault =
      data?.urlFilter && Object.keys(data?.urlFilter).length !== 0
        ? transformUrlFilterToForms(data.urlFilter)
        : [{ control: "contains", include: ["/"], excludes: [] }];

    const withRepeatDefault =
      data?.repeatNotification !== undefined ? data?.repeatNotification : true;

    const iconColorDefault =
      data?.notificationVisualSettings?.iconColor ??
      branding?.iconColor ??
      NotificationDefaultSettings.IconColor;

    const showIconDefault =
      data?.notificationVisualSettings?.isShowingIcon !== null &&
      typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true;

    const defaultIconBackgroundColor =
      data?.notificationVisualSettings?.iconBackgroundColor ??
      branding?.iconBackgroundColor ??
      iconBackgroundColor ??
      NotificationDefaultSettings.IconBackgroundColor;

    if (
      borderRadius !== null &&
      (data?.notificationVisualSettings?.roundness !== null &&
      data?.notificationVisualSettings?.roundness !== undefined
        ? borderRadius !== data?.notificationVisualSettings?.roundness
        : borderRadius !== branding?.borderRadius)
    ) {
      setDetectedChange(true);
    } else if (
      backgroundOpacity !== null &&
      (data?.notificationVisualSettings?.backgroundOpacity !== null &&
      data?.notificationVisualSettings?.backgroundOpacity !== undefined
        ? backgroundOpacity !==
          data?.notificationVisualSettings?.backgroundOpacity
        : backgroundOpacity !== branding?.backgroundOpacity)
    ) {
      setDetectedChange(true);
    } else if (backgroundColor && backgroundColor !== defaultBackgroundColor) {
      setDetectedChange(true);
    } else if (textColor && textColor !== defaultTextColor) {
      setDetectedChange(true);
    } else if (activeDefault && activeDefault !== active) {
      setDetectedChange(true);
    } else if (showIcon !== showIconDefault) {
      setDetectedChange(true);
    } else if (
      iconBackgroundColor &&
      iconBackgroundColor !== defaultIconBackgroundColor
    ) {
      setDetectedChange(true);
    } else if (
      iconBackgroundRadius &&
      (data?.notificationVisualSettings?.iconBackgroundRadius !== null &&
      data?.notificationVisualSettings?.iconBackgroundRadius !== undefined
        ? iconBackgroundRadius !==
          data?.notificationVisualSettings?.iconBackgroundRadius
        : iconBackgroundRadius !== branding?.iconBackgroundRadius)
    ) {
      setDetectedChange(true);
    } else if (iconColor && iconColor !== iconColorDefault) {
      setDetectedChange(true);
    } else if (
      icon &&
      (data?.notificationVisualSettings?.icon
        ? icon !== data?.notificationVisualSettings?.icon
        : icon !== NotificationDefaultSettings.OrdersIcon)
    ) {
      setDetectedChange(true);
    } else if (withRepeat !== withRepeatDefault) {
      setDetectedChange(true);
    } else if (
      JSON.stringify(formsDefault) !== JSON.stringify(urlFilterForms)
    ) {
      setDetectedChange(true);
    } else if (days !== daysDefault) {
      setDetectedChange(true);
    } else if (minNumber !== minNumberDefault) {
      setDetectedChange(true);
    } else if (selectedVariant !== selectedVariantDefault) {
      setDetectedChange(true);
    } else if (textDefault !== text) {
      setDetectedChange(true);
    } else {
      setDetectedChange(false);
    }
  }, [
    borderRadius,
    withRepeat,
    text,
    active,
    days,
    minNumber,
    selectedVariant,
    urlFilterForms,
    icon,
    textColor,
    iconColor,
    backgroundColor,
    backgroundOpacity,
    showIcon,
    iconBackgroundColor,
    iconBackgroundRadius,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.roundness,
    branding?.brandingIsShowingIcon,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconColor,
    branding?.activeSize,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
  ]);

  const onCloseAndClearInputs = () => {
    setSelectedTab("rules");
    setText(t(getActualVisitorsModalInitialText(language)) ?? "");
    setWithRepeat(true);
    setDays(7);
    setMinNumber(20);
    setUrlFilterForms([{ control: "contains", include: ["/"], excludes: [] }]);
    onClose();
  };

  useEffect(() => {
    setBackgroundColor(
      data?.notificationVisualSettings?.backgroundColor ??
        branding?.backgroundColor ??
        null,
    );
    setTextColor(
      data?.notificationVisualSettings?.textColor ??
        branding?.textColor ??
        null,
    );
    setIconBackgroundColor(
      data?.notificationVisualSettings?.iconBackgroundColor ??
        branding?.iconBackgroundColor ??
        null,
    );
    setBorderRadius(
      data?.notificationVisualSettings?.roundness ??
        branding?.borderRadius ??
        null,
    );
    setBackgroundOpracity(
      data?.notificationVisualSettings?.backgroundOpacity ??
        branding?.backgroundOpacity ??
        null,
    );
    setActive(
      data?.notificationVisualSettings?.activeSize ||
        branding?.activeSize ||
        "classic",
    );
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : true,
    );
    setIcon(data?.notificationVisualSettings?.icon ?? null);
    setIconBackgroundRadius(
      data?.notificationVisualSettings?.iconBackgroundRadius ??
        branding?.iconBackgroundRadius ??
        null,
    );
  }, [
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.textColor,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    branding?.backgroundColor,
    branding?.textColor,
    branding?.iconBackgroundColor,
    branding?.borderRadius,
    branding?.backgroundOpacity,
    branding?.activeSize,
    branding?.iconBackgroundRadius,
  ]);

  useLayoutEffect(() => {
    const createNotificationPreviewHtml = async () => {
      const result = await window.getNotificationPreview(
        {
          campaign: {
            notificationVisualSettings: {
              roundness: 1,
            },
          },
          settings: {
            type: selectedVariant,
            notificationVisualSettings: {
              backgroundColor:
                backgroundColor ??
                data?.notificationVisualSettings?.backgroundColor ??
                branding?.backgroundColor ??
                "",
              textColor:
                textColor ??
                data?.notificationVisualSettings?.textColor ??
                branding?.textColor ??
                "",
              iconBackgroundColor:
                iconBackgroundColor ??
                data?.notificationVisualSettings?.iconBackgroundColor ??
                branding?.iconBackgroundColor ??
                "",
              iconColor:
                iconColor ??
                data?.notificationVisualSettings?.iconColor ??
                branding?.iconColor ??
                "",
              icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
              roundness:
                borderRadius ??
                data?.notificationVisualSettings?.roundness ??
                branding?.borderRadius ??
                0,
              linkTextColor: "",
              linkButtonBackgroundColor: "",
              backgroundOpacity:
                backgroundOpacity ??
                data?.notificationVisualSettings?.backgroundOpacity ??
                branding?.backgroundOpacity ??
                1,
              activeSize:
                active ??
                data?.notificationVisualSettings?.activeSize ??
                branding?.activeSize ??
                "classic",
              isShowingIcon:
                showIcon ??
                data?.notificationVisualSettings?.isShowingIcon ??
                branding?.brandingIsShowingIcon ??
                true,
              iconBackgroundRadius:
                iconBackgroundRadius ??
                data?.notificationVisualSettings?.iconBackgroundRadius ??
                branding?.iconBackgroundRadius ??
                0,
              customWebBranding,
            },
          },
        },
        selectedVariant,
        undefined,
        text,
        undefined,
        language,
        minNumber,
        days,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        notificationStyleId,
      );
      // Transforms object htmldivelement to pure string
      const cleanHtmlString = DOMPurify.sanitize(result, {
        USE_PROFILES: { html: true, svg: true },
      });
      setNotificationHtml(cleanHtmlString);
    };

    createNotificationPreviewHtml();
  }, [
    active,
    backgroundColor,
    backgroundOpacity,
    borderRadius,
    branding?.activeSize,
    branding?.backgroundColor,
    branding?.backgroundOpacity,
    branding?.borderRadius,
    branding?.brandingIsShowingIcon,
    branding?.iconBackgroundColor,
    branding?.iconBackgroundRadius,
    branding?.iconColor,
    branding?.textColor,
    data?.notificationVisualSettings?.activeSize,
    data?.notificationVisualSettings?.backgroundColor,
    data?.notificationVisualSettings?.backgroundOpacity,
    data?.notificationVisualSettings?.icon,
    data?.notificationVisualSettings?.iconBackgroundColor,
    data?.notificationVisualSettings?.iconBackgroundRadius,
    data?.notificationVisualSettings?.iconColor,
    data?.notificationVisualSettings?.isShowingIcon,
    data?.notificationVisualSettings?.roundness,
    data?.notificationVisualSettings?.textColor,
    days,
    icon,
    iconBackgroundColor,
    iconBackgroundRadius,
    iconColor,
    language,
    minNumber,
    selectedVariant,
    showIcon,
    text,
    textColor,
  ]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue as TabType);
  };

  const onSave = async () => {
    try {
      const _notificationManager = NotificationManager.getManager();
      let newCreatedNotificationId = undefined;
      if (data?.type !== selectedVariant) {
        await _notificationManager.deleteNotificationSettings(
          createdNotificationId,
        );
        newCreatedNotificationId = await createNotificationPrimitive(
          selectedVariant,
        );
      }
      const body: UpdateNotificationSettingsRequest = {
        id: newCreatedNotificationId?.id
          ? newCreatedNotificationId?.id
          : createdNotificationId,
        isActive: true,
        repeatNotification: withRepeat,
        text: text,
        maxLastDays: days,
        minVisitors:
          selectedVariant === "visitorSummary" ? minNumber : undefined,
        minRegistrations:
          selectedVariant === "registrationSummary" ? minNumber : undefined,
        minOrders: selectedVariant === "orderSummary" ? minNumber : undefined,
        urlFilter: transformFormsToUrlFilterDto(urlFilterForms),

        notificationVisualSettings: {
          backgroundColor: backgroundColor ?? branding?.backgroundColor ?? "",
          textColor: textColor ?? branding?.textColor ?? "",
          iconBackgroundColor:
            iconBackgroundColor ?? branding?.iconBackgroundColor ?? "",
          iconColor: iconColor ?? branding?.iconColor ?? "",
          icon: icon ?? data?.notificationVisualSettings?.icon ?? "",
          roundness: borderRadius ?? branding?.borderRadius ?? 0,
          linkTextColor: "",
          linkButtonBackgroundColor: "",
          backgroundOpacity:
            backgroundOpacity ?? branding?.backgroundOpacity ?? 1,
          activeSize: active ?? branding?.activeSize ?? "classic",
          isShowingIcon: showIcon ?? branding?.brandingIsShowingIcon ?? true,
          iconBackgroundRadius:
            iconBackgroundRadius ?? branding?.iconBackgroundRadius ?? 0,
        },
      };

      await _notificationManager.updateNotificationSettingsAsync(body);
      onSaveSuccess?.();
      onCloseAndClearInputs();
    } catch (e: any) {
      showFlashMessageWithTimeout(dispatch, e.toString(), "error");
    }
  };

  const onReset = () => {
    setActive(
      data?.notificationVisualSettings?.activeSize ??
        branding?.activeSize ??
        "classic",
    );
    setBackgroundColor(null);
    setTextColor(null);
    setBorderRadius(null);
    setBackgroundOpracity(null);
    setShowIcon(
      data?.notificationVisualSettings?.isShowingIcon !== null &&
        typeof data?.notificationVisualSettings?.isShowingIcon !== "undefined"
        ? !!data?.notificationVisualSettings?.isShowingIcon
        : branding?.brandingIsShowingIcon !== null &&
          typeof branding?.brandingIsShowingIcon !== "undefined"
        ? !!branding?.brandingIsShowingIcon
        : true,
    );
    setIconBackgroundColor(null);
    setIconBackgroundRadius(null);
    setIconColor(null);
    setIcon(null);
    setWithRepeat(
      data?.repeatNotification !== undefined ? data?.repeatNotification : true,
    );
    setUrlFilterForms(
      data?.urlFilter && Object.keys(data?.urlFilter).length !== 0
        ? transformUrlFilterToForms(data.urlFilter)
        : [
            {
              control: "beginsWith",
              include: [],
              excludes: [],
            },
          ],
    );
    setDays(data?.maxLastDays !== undefined ? data?.maxLastDays : 7);
    setMinNumber(data !== undefined ? getMin(data) : 20);
    setSelectedVariant(
      data?.type ? (data.type as VariantType) : "visitorSummary",
    );
    setText(
      data?.text
        ? data.text
        : t(getActualVisitorsModalInitialText(language)) ?? "",
    );
  };

  const tabs = [
    {
      value: "settings",
      label: t("campaign.notifications.settings.customization.title"),
      content: (
        <NotificationSettingsTab
          notificationType='summaryModal'
          setText={setText}
          text={text}
          selectedVariant={selectedVariant}
          setSelectedVariant={setSelectedVariant as any}
        />
      ),
    },
    {
      value: "rules",
      label: t("campaign.notifications.settings.displayRules.title"),
      content: (
        <NotificationRulesTab
          rulesType='summaryModal'
          selectedVariant={selectedVariant}
          days={days}
          min={minNumber}
          withRepeat={withRepeat}
          onChangeDays={val => setDays(val)}
          onChangeMin={val => setMinNumber(val)}
          onToggleWithRepeat={() => setWithRepeat(!withRepeat)}
          t={t}
        />
      ),
    },
    {
      value: "pairing",
      label: t("campaign.notifications.settings.pairing.title"),
      content: (
        <NotificationPairingTab
          addEmptyPairingForm={addEmptyPairingForm}
          setForms={setUrlFilterForms}
          forms={urlFilterForms}
          forOrders={false}
          handlePairingFormControlChange={handlePairingFormControlChange}
          onAddPairingFormExcludeChip={onAddPairingFormExcludeChip}
          onAddPairingFormIncludeChip={onAddPairingFormIncludeChip}
          onDeletePairingForm={onDeletePairingForm}
          type='summaryModal'
          selectedVariant={selectedVariant}
        />
      ),
      visible: false,
    },
    {
      value: "design",
      label: t("campaign.notifications.settings.design.title"),
      content: (
        <NotificationDesignTab
          active={active}
          data={data}
          defaultIcon={NotificationDefaultSettings.VisitIcon}
          backgroundColor={backgroundColor}
          backgroundOpacity={backgroundOpacity}
          borderRadius={borderRadius}
          branding={branding}
          iconBackgroundColor={iconBackgroundColor}
          iconBackgroundRadius={iconBackgroundRadius}
          setActive={setActive}
          setBackgroundColor={setBackgroundColor}
          setBackgroundOpracity={setBackgroundOpracity}
          setBorderRadius={setBorderRadius}
          setIconBackgroundColor={setIconBackgroundColor}
          setIconBackgroundRadius={setIconBackgroundRadius}
          setShowIcon={setShowIcon}
          setTextColor={setTextColor}
          showIcon={showIcon}
          textColor={textColor}
          disableIcon={false}
          icon={icon}
          iconColor={iconColor}
          setIcon={setIcon}
          setIconColor={setIconColor}
        />
      ),
    },
  ];

  return (
    <NotificationModalBase
      title={t("campaign.notifications.visitorSummary.title")}
      detectedChange={detectedChange}
      isOpen={isOpen}
      isNewNotification={!data}
      notificationHtml={notificationHtml}
      onClose={onClose}
      onReset={onReset}
      onSave={onSave}
      previewModalOpen={previewModalOpen}
      setPreviewModalOpen={setPreviewModalOpen}
    >
      <Tabs
        onTabChange={handleTabChange}
        selectedTab={selectedTab}
        tabs={tabs}
      />
    </NotificationModalBase>
  );
};

export default SummaryModal;
